import React from "react";
import { Saddle, useTheme } from "@tvg/design-system";
import {
  RacePool,
  BettingInterest,
  BettingInterestPool,
  BettingInterestPoolsRunners
} from "@tvg/ts-types/Race";
import { Tr, Td, SaddleNumberContainer } from "../styled-components";
import { MAX_AMOUNT } from "../constants";
import { getPools, getPoolRunnersDataAmount } from "../handlers";
import TooltipExceededAmounts from "./TooltipExceededAmounts";

interface Props {
  racePools: RacePool[];
  bettingInterests: BettingInterest[];
  wagerTypeLimit: number;
  currentUnit: string;
}

export const renderSaddleRunner = (
  runnerNumber: string,
  bi: BettingInterest,
  isRunnerScratched: boolean,
  numberColor: string | undefined,
  saddleColor: string | undefined
) => (
  <Td
    key={`pools-column-biNumber-${runnerNumber}`}
    data-qa-label={`pools-column-biNumber-${bi.biNumber}`}
  >
    <Saddle
      number={runnerNumber}
      numberColor={numberColor}
      saddleColor={saddleColor}
      size="s"
      isScratched={isRunnerScratched}
    />
  </Td>
);

export const renderValidRunners = (
  bi: BettingInterest,
  isCoupledRunner: boolean | undefined,
  numberColor: string | undefined,
  saddleColor: string | undefined
) =>
  bi.runners?.map((biRunner) => {
    const isRunnerScratched = biRunner.scratched;
    const runnerNumber = biRunner.runnerId;
    const isValidCoupledRunner = isCoupledRunner && !isRunnerScratched;

    return (
      (isValidCoupledRunner || !isCoupledRunner) &&
      renderSaddleRunner(
        runnerNumber,
        bi,
        isRunnerScratched,
        numberColor,
        saddleColor
      )
    );
  });

export const renderScratchedCoupledRunner = (
  bettingInterest: BettingInterest,
  bi: BettingInterest,
  numberColor: string | undefined,
  saddleColor: string | undefined
) => {
  const isRunnerScratched = !!bettingInterest?.runners[0].scratched;
  const runnerNumber = bi.runners[0].runnerId;

  return renderSaddleRunner(
    runnerNumber,
    bi,
    isRunnerScratched,
    numberColor,
    saddleColor
  );
};

export const RunnersPools = ({
  racePools,
  bettingInterests,
  wagerTypeLimit,
  currentUnit
}: Props) => {
  const { colors } = useTheme();
  if (bettingInterests.length === 0) {
    return null;
  }

  return (
    <tbody data-qa-label="pools-table-body">
      {bettingInterests.map((bi: BettingInterest) => {
        const allBiScratched = bi.runners?.every(
          (runner: BettingInterestPoolsRunners) => runner.scratched
        );

        const biPools = getPools(
          racePools,
          bi.biPools,
          wagerTypeLimit,
          currentUnit
        );

        if (biPools.length === 0) {
          return null;
        }

        const bettingInterest = bettingInterests?.find(
          (bInterest: BettingInterest) => bInterest.biNumber === bi.biNumber
        );
        const saddleColor = bettingInterest?.saddleColor;
        const numberColor = bettingInterest?.numberColor;
        const isCoupledRunner =
          bettingInterest?.runners && bettingInterest?.runners.length > 1;
        const isAllCoupledRunnersScratched = isCoupledRunner && allBiScratched;

        const renderRunners = isAllCoupledRunnersScratched
          ? renderScratchedCoupledRunner(
              bettingInterest,
              bi,
              numberColor,
              saddleColor
            )
          : renderValidRunners(bi, isCoupledRunner, numberColor, saddleColor);

        return (
          <Tr
            key={`pools-row-biNumber-${bi.biNumber}`}
            data-qa-label={`pools-row-biNumber-${bi.biNumber}`}
            backgroundColor={colors.white["900"]}
          >
            <SaddleNumberContainer>{renderRunners}</SaddleNumberContainer>

            {biPools.map(
              (pool: BettingInterestPool) =>
                pool.wagerType.id <= wagerTypeLimit && (
                  <Td
                    key={`pools-column-${pool.wagerType.code}-biNumber-${bi.biNumber}`}
                    data-qa-label={`pools-column-${pool.wagerType.code}-biNumber-${bi.biNumber}`}
                  >
                    {!allBiScratched &&
                    currentUnit === "USD" &&
                    pool.poolRunnersData[0]?.amount > MAX_AMOUNT ? (
                      <TooltipExceededAmounts
                        amount={pool.poolRunnersData[0].amount}
                      />
                    ) : (
                      getPoolRunnersDataAmount(
                        allBiScratched,
                        pool.poolRunnersData,
                        currentUnit
                      )
                    )}
                  </Td>
                )
            )}
          </Tr>
        );
      })}
    </tbody>
  );
};

export default RunnersPools;
